import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { VARIABLES } from '@environments/environment.variables';
import { CurrentLanguageService } from '@shared/services/language/current-language.service';
import { LogLevel } from '@shared/services/logs/enums';
import { PlatformContextService } from '@shared/services/platform-context';
import { envConfiguredNetworkId } from '@shared/utils';
import { fromEvent, merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GcpLogsApiService } from './gcp-logs-api.service';
import { GcpLogBody, GcpLogLevel, GcpLogParams } from './models';

type ConsoleLevel = 'info' | 'warn' | 'error' | 'log';

interface LogOptions {
  gcp?: boolean;
  /** Console display only: renders the message text dimmed, for background-noise logs (cache/mock hits). */
  muted?: boolean;
}

interface NavigationHistoryEntry {
  url: string;
  timestamp: string;
}

interface GcpDedupEntry {
  params: GcpLogParams;
  body: GcpLogBody;
  count: number;
  timeoutId: ReturnType<typeof setTimeout>;
}

const MAX_NAVIGATION_HISTORY = 10;
const GCP_NAMESPACE = 'socooc';
const GCP_DEDUP_DELAY_MS = 5_000;
const GCP_DATA_MAX_DEPTH = 5;
const GCP_DATA_MAX_BYTES = 4_096;
const GCP_STRIPPED_KEYS = new Set(['trace', 'normalizedNames', 'lazyUpdate']);
/** Auth/credential query params that must never reach GCP (extend when a new sensitive param appears). */
const GCP_REDACTED_QUERY_PARAMS = ['token'];

const CONSOLE_LEVEL_STYLE: Readonly<Record<ConsoleLevel, string>> = {
  info: 'background-color:#e7f5ff;border-color:#d0ebff;color:#228be6',
  warn: 'background-color:#fff9db;border-color:#ffe066;color:#fab005',
  error: 'background-color:#fff5f5;border-color:#ffa8a8;color:#fa5252',
  log: 'background-color:#f8f9fa;border-color:#e9ecef;color:#868e96',
};

const MUTED_MESSAGE_STYLE = 'color:#868e96;font-style:italic';
const MUTED_CHIP_STYLE = 'background-color:#f8f9fa;border-color:#dee2e6;color:#adb5bd';

const LOG_LEVEL_TO_GCP: Readonly<Record<string, GcpLogLevel>> = {
  fatal: 'critical',
  error: 'error',
  warn: 'warning',
  info: 'info',
  debug: 'debug',
  trace: 'debug',
  all: 'debug',
};

/**
 * Centralized client-side logging service.
 *
 * - All methods are no-ops on the server.
 * - Styled location labels appear from `INFO` and above.
 * - FATAL/ERROR are auto-forwarded to GCP (opt-out with `{ gcp: false }`).
 * - Other levels can opt-in with `{ gcp: true }` as the last parameter.
 * - `{ muted: true }` dims the whole console line, lightened chip and gray italic text (display only).
 * - GCP forwarding is disabled in LOCAL environments and on unbuilt sessions (`buildTimestamp === 0`).
 * - Duplicate errors within a 5s window are deduplicated, and sent at once (keepalive) when the page is hidden.
 */
@Injectable({ providedIn: 'root' })
export class LogsService {
  /**
   * Singleton instance captured during construction. Used by the static API
   * (`LogsService.error`, etc.) to bridge to the full-featured instance
   * methods (GCP forwarding, dedup, navigation history). When the instance
   * is not yet available (e.g. in `main.ts`, `server.ts`, or before
   * bootstrap), the static API falls back to `console.*`.
   */
  private static instance: LogsService | undefined;

  private readonly platformContextService = inject(PlatformContextService);
  private readonly gcpLogsApi = inject(GcpLogsApiService);
  private readonly router = inject(Router, { optional: true });
  private readonly language = inject(CurrentLanguageService).language;

  private sendingToGcp = false;
  private readonly sessionId = this.platformContextService.isServer() ? '' : crypto.randomUUID();
  private readonly loadTime = Date.now();
  private readonly navigationHistory: NavigationHistoryEntry[] = [];
  private navigationCount = 0;
  private readonly gcpDedupBuffer = new Map<string, GcpDedupEntry>();

  constructor() {
    LogsService.instance = this;
    if (!this.platformContextService.isServer() && this.router) {
      this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((event) => {
        this.navigationCount++;
        this.navigationHistory.push({
          url: redactSensitiveParams(event.urlAfterRedirects),
          timestamp: formatTime(new Date()),
        });
        if (this.navigationHistory.length > MAX_NAVIGATION_HISTORY) {
          this.navigationHistory.shift();
        }
      });
    }
    if (!this.platformContextService.isServer()) {
      merge(
        fromEvent(window, 'pagehide'),
        fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'hidden'))
      )
        .pipe(takeUntilDestroyed())
        .subscribe(() => this.flushAllGcpEntries());
    }
  }

  /**
   * Static fallback API. Mirrors the instance methods (`fatal`, `error`, ...)
   * but is callable outside an Angular DI context (pure utils, `main.ts`,
   * `server.ts`). If the singleton is available, delegates to it (so logs
   * still flow to GCP). Otherwise prints to the console directly.
   *
   * The fallback output is why `no-console` is turned off for this whole
   * file in `eslint.config.mjs`.
   */
  static fatal(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (LogsService.instance) {
      LogsService.instance.fatal(location, message, ...optionalParams);
    } else {
      LogsService.consoleFallback('error', location, message, ...optionalParams);
    }
  }

  static error(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (LogsService.instance) {
      LogsService.instance.error(location, message, ...optionalParams);
    } else {
      LogsService.consoleFallback('error', location, message, ...optionalParams);
    }
  }

  static warn(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (LogsService.instance) {
      LogsService.instance.warn(location, message, ...optionalParams);
    } else {
      LogsService.consoleFallback('warn', location, message, ...optionalParams);
    }
  }

  static info(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (LogsService.instance) {
      LogsService.instance.info(location, message, ...optionalParams);
    } else {
      LogsService.consoleFallback('info', location, message, ...optionalParams);
    }
  }

  static debug(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (LogsService.instance) {
      LogsService.instance.debug(location, message, ...optionalParams);
    } else {
      LogsService.consoleFallback('log', location, message, ...optionalParams);
    }
  }

  static trace(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (LogsService.instance) {
      LogsService.instance.trace(location, message, ...optionalParams);
    } else {
      LogsService.consoleFallback('log', location, `* ${String(message)}`, ...optionalParams);
    }
  }

  static all(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (LogsService.instance) {
      LogsService.instance.all(location, message, ...optionalParams);
    } else {
      LogsService.consoleFallback('log', location, message, ...optionalParams);
    }
  }

  private static consoleFallback(
    consoleLevel: 'error' | 'warn' | 'info' | 'log',
    location: string,
    message: unknown,
    ...optionalParams: unknown[]
  ): void {
    console[consoleLevel](`[${location}]`, message, ...optionalParams);
  }

  /** Log niveau FATAL. Auto-forwarded to GCP. */
  fatal(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.FATAL) {
      this.logger('error', 'fatal', true, location, message, ...optionalParams);
    }
  }

  /** Log niveau ERROR. Auto-forwarded to GCP. */
  error(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.ERROR) {
      this.logger('error', 'error', true, location, message, ...optionalParams);
    }
  }

  /** Log niveau WARN. */
  warn(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.WARN) {
      this.logger('warn', 'warn', false, location, message, ...optionalParams);
    }
  }

  /** Log niveau INFO. */
  info(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.INFO) {
      this.logger('info', 'info', false, location, message, ...optionalParams);
    }
  }

  /** Log niveau DEBUG. */
  debug(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.DEBUG) {
      this.logger('log', 'debug', false, location, message, ...optionalParams);
    }
  }

  /** Log niveau TRACE. Message prefixed with "* ". */
  trace(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.TRACE) {
      this.logger('log', 'trace', false, location, `* ${String(message)}`, ...optionalParams);
    }
  }

  /** Log niveau ALL (most permissive). */
  all(location: string, message: unknown, ...optionalParams: unknown[]): void {
    if (environment.logLevel >= LogLevel.ALL) {
      this.logger('log', 'all', false, location, message, ...optionalParams);
    }
  }

  /** Opens a console group if the current log level allows it. */
  groupStart(
    level: LogLevel,
    isCollapsed: boolean,
    location: string,
    message: unknown,
    ...optionalParams: unknown[]
  ): void {
    if (this.platformContextService.isServer()) return;
    if (environment.logLevel >= level) {
      const locationDisplayed = this.getLocationDisplayed('log', location);
      if (isCollapsed) {
        console.groupCollapsed(...locationDisplayed, message, ...optionalParams);
      } else {
        console.group(...locationDisplayed, message, ...optionalParams);
      }
    }
  }

  /** Closes the most recently opened console group. */
  groupEnd(level: LogLevel): void {
    if (this.platformContextService.isServer()) return;
    if (environment.logLevel >= level) {
      console.groupEnd();
    }
  }

  /**
   * Reads `sessionStorage['LogLevel']` and updates `environment.logLevel` for the session.
   * @returns `true` if the log level was updated, `false` otherwise.
   */
  updateLogLevelFromSession(): boolean {
    if (this.platformContextService.isServer()) return false;

    try {
      const raw = sessionStorage.getItem('LogLevel');
      if (!raw) return false;

      const parsed = Number.parseInt(raw, 10);
      if (Number.isFinite(parsed) && (LogLevel as unknown as Record<number, string>)[parsed]) {
        environment.logLevel = parsed;
        console.info(
          `%c📜 Logs display forced to "${(LogLevel as unknown as Record<number, string>)[environment.logLevel]}" for this session.`,
          'color:#f06595;font-weight:bold'
        );
        console.log(`🏷️ Environment`, environment);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private logger(
    consoleLevel: ConsoleLevel,
    logMethod: string,
    autoGcp: boolean,
    location: string,
    message: unknown,
    ...optionalParams: unknown[]
  ): void {
    if (this.platformContextService.isServer()) return;

    const { options, params } = this.extractOptions(optionalParams);
    const shouldForwardToGcp = options?.gcp ?? autoGcp;
    const locationDisplayed = this.getLocationDisplayed(consoleLevel, location, options?.muted === true);

    if (options?.muted && typeof message === 'string') {
      // %c directives are only parsed in the first console argument: fold the message into it to style it
      if (locationDisplayed.length === 2) {
        console[consoleLevel](
          `${locationDisplayed[0]}%c ${message}`,
          locationDisplayed[1],
          MUTED_MESSAGE_STYLE,
          ...params
        );
      } else {
        console[consoleLevel](`%c${message}`, MUTED_MESSAGE_STYLE, ...params);
      }
    } else {
      console[consoleLevel](...locationDisplayed, message, ...params);
    }

    if (shouldForwardToGcp) {
      this.sendToGcp(logMethod, location, message, params);
    }
  }

  private getLocationDisplayed(level: ConsoleLevel, location: string, muted = false): [string, string] | [] {
    if (environment.logLevel < LogLevel.INFO) return [];
    const base = 'font-size:8pt;padding:4px 6px;border-radius:4px;border-style:solid;border-width:1px;';
    const color = muted ? MUTED_CHIP_STYLE : (CONSOLE_LEVEL_STYLE[level] ?? CONSOLE_LEVEL_STYLE.log);
    return [`%c${location}`, `${base}${color}`];
  }

  private extractOptions(params: unknown[]): { options: LogOptions | undefined; params: unknown[] } {
    if (params.length === 0) return { options: undefined, params };
    const last = params[params.length - 1];
    if (this.isLogOptions(last)) {
      return { options: last, params: params.slice(0, -1) };
    }
    return { options: undefined, params };
  }

  private isLogOptions(candidate: unknown): candidate is LogOptions {
    if (typeof candidate !== 'object' || candidate === null) return false;
    const entries = Object.entries(candidate);
    if (entries.length === 0) return false;
    return entries.every(([key, value]) => (key === 'gcp' || key === 'muted') && typeof value === 'boolean');
  }

  /**
   * Buffers a log entry for GCP with deduplication (5s window).
   * Disabled in LOCAL environments and on unbuilt sessions (buildTimestamp === 0).
   */
  private sendToGcp(logMethod: string, location: string, message: unknown, data: unknown[]): void {
    if (environment.applicationType === 'LOCAL') return;
    if (VARIABLES.buildTimestamp === 0) return;
    if (this.sendingToGcp) return;

    const dedupKey = `${location}::${String(message)}`;
    const existing = this.gcpDedupBuffer.get(dedupKey);

    if (existing) {
      existing.count++;
      return;
    }

    // An Error passed in message position must also yield its real stack
    const errorStack = [message, ...data].find((item): item is Error => item instanceof Error)?.stack;
    const networkId = String(envConfiguredNetworkId(this.language.region));
    const gcpLevel = LOG_LEVEL_TO_GCP[logMethod] ?? 'debug';
    const isErrorLevel = gcpLevel === 'critical' || gcpLevel === 'error';
    const stack = errorStack ?? (isErrorLevel ? this.synthesizeStack(logMethod) : undefined);

    const params: GcpLogParams = { namespace: GCP_NAMESPACE, networkId, level: gcpLevel };

    const body: GcpLogBody = {
      eventTime: new Date().toISOString(),
      message: String(message),
      ...(stack ? { stack } : {}),
      serviceContext: { version: VARIABLES.version || 'unknown' },
      context: {
        location,
        url: redactSensitiveParams(window.location.pathname + window.location.search + window.location.hash),
        language: this.language.locale,
        sessionId: this.sessionId,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timeSinceLoadMs: Date.now() - this.loadTime,
        buildDate: this.formatBuildDate(),
        ...(VARIABLES.commitHash ? { commitHash: VARIABLES.commitHash } : {}),
        userAgent: navigator.userAgent,
        visibilityState: document.visibilityState,
        // `hidden@-1200`: the page turned hidden 1.2 s before this error (Chromium only)
        visibilityHistory: performance
          .getEntriesByType('visibility-state')
          .map((entry) => `${entry.name}@${Math.round(entry.startTime - performance.now())}`),
        prerendering: (document as Document & { prerendering?: boolean }).prerendering,
        online: navigator.onLine,
        navigationHistory: [...this.navigationHistory],
        ...(this.navigationCount > this.navigationHistory.length
          ? { navigationHistoryDropped: this.navigationCount - this.navigationHistory.length }
          : {}),
        ...(data.length > 0 ? { data: this.sanitizeForGcp(data) } : {}),
      },
    };

    const timeoutId = setTimeout(() => this.flushGcpEntry(dedupKey), GCP_DEDUP_DELAY_MS);
    this.gcpDedupBuffer.set(dedupKey, { params, body, count: 1, timeoutId });
  }

  private flushAllGcpEntries(): void {
    for (const [dedupKey, { timeoutId }] of this.gcpDedupBuffer) {
      clearTimeout(timeoutId);
      this.flushGcpEntry(dedupKey, true);
    }
  }

  private flushGcpEntry(dedupKey: string, keepalive = false): void {
    const buffered = this.gcpDedupBuffer.get(dedupKey);
    this.gcpDedupBuffer.delete(dedupKey);
    if (!buffered) return;

    if (buffered.count > 1) {
      buffered.body.context['occurrences'] = buffered.count;
    }

    this.sendingToGcp = true;
    this.gcpLogsApi.send(buffered.params, buffered.body, keepalive);
    setTimeout(() => (this.sendingToGcp = false), 0);
  }

  private formatBuildDate(): string {
    const timestamp = VARIABLES.buildTimestamp;
    return timestamp === 0 ? 'local' : new Date(timestamp).toISOString();
  }

  /**
   * Builds a stack trace for error/fatal logs sent without an `Error` object:
   * Error Reporting needs V8 frames in `stack` to group occurrences. The
   * service's own frames are hidden (`captureStackTrace`, V8 and recent
   * browsers) so the first frame points at the actual call site.
   *
   * Known limit: when entering through the static API, one constant wrapper
   * frame (`LogsService.error`/`fatal`) remains in first position; grouping
   * still works (the call-site frame right below differs per origin).
   */
  private synthesizeStack(logMethod: string): string | undefined {
    const syntheticError = new Error();
    // eslint-disable-next-line @typescript-eslint/unbound-method -- function identity used as a stack marker, never invoked
    Error.captureStackTrace?.(syntheticError, logMethod === 'fatal' ? this.fatal : this.error);
    return syntheticError.stack;
  }

  private sanitizeForGcp(data: unknown[]): unknown[] {
    const sanitized = data.map((item) => this.deepSanitize(item, 0));
    try {
      if (JSON.stringify(sanitized).length > GCP_DATA_MAX_BYTES) {
        return [{ _truncated: true, _message: `data exceeded ${GCP_DATA_MAX_BYTES} bytes after sanitization` }];
      }
    } catch {
      return [{ _truncated: true, _message: 'data could not be serialized' }];
    }
    return sanitized;
  }

  private deepSanitize(value: unknown, depth: number): unknown {
    if (depth > GCP_DATA_MAX_DEPTH) return '[max depth]';
    if (value === null || value === undefined) return value;
    if (typeof value !== 'object') return value;

    if (Array.isArray(value)) {
      return value.map((item) => this.deepSanitize(item, depth + 1));
    }

    if (value instanceof Error || value instanceof DOMException) {
      return { name: value.name, message: value.message };
    }

    const obj = value as Record<string, unknown>;

    // Compact Angular HttpErrorResponse to essential fields only
    if (obj['name'] === 'HttpErrorResponse' && 'status' in obj) {
      const error = obj['error'] as Record<string, unknown> | undefined;
      return {
        status: obj['status'],
        url: obj['url'],
        message: obj['message'],
        ...(error instanceof Error || error instanceof DOMException
          ? { cause: { name: error.name, message: error.message } }
          : {}),
        ...(error?.['detail'] ? { errorDetail: error['detail'] } : {}),
      };
    }

    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(obj)) {
      if (key === 'trace' && Array.isArray(val)) continue;
      if (GCP_STRIPPED_KEYS.has(key)) continue;
      result[key] = this.deepSanitize(val, depth + 1);
    }
    return result;
  }
}

function redactSensitiveParams(url: string): string {
  if (!url.includes('?')) return url;
  const parsed = new URL(url, 'http://placeholder');
  let redacted = false;
  for (const param of GCP_REDACTED_QUERY_PARAMS) {
    if (parsed.searchParams.has(param)) {
      parsed.searchParams.delete(param);
      redacted = true;
    }
  }
  if (!redacted) return url;
  const query = parsed.searchParams.size > 0 ? `?${parsed.searchParams.toString()}` : '';
  return `${parsed.pathname}${query}${parsed.hash}`;
}

function formatTime(date: Date): string {
  const hh = date.getHours().toString().padStart(2, '0');
  const mm = date.getMinutes().toString().padStart(2, '0');
  const ss = date.getSeconds().toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}
